import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { ReactComponent as SlideIcon } from '@assets/icons/order/slide-icon.svg';

import CustomDropdown from '@components/CustomDropdown';
import { toastError } from '@components/Toast';

import BlockUsedFrom from '@features/measurement/components/BlockUsedFrom';
import PomViewModal from '@features/pom/components/PomViewModal';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import {
	addMeasurementGroupGrading,
	changeCostingPpSampleBaseSize,
	fetchProductWiseMeasurementSizeByCategoryId,
	modifyMeasurementTableCellValue,
	modifyMeasurementTablePointCode,
	modifyMeasurementTablePointIds,
	modifyMeasurementTablePointName,
	pasteMeasurementSizeValues,
	reArrangeMeasurementTable,
	removeMeasurementGroupGrading,
	removeMeasurementSize,
	removePointInMeasurementTable,
	saveProductMeasurementBulkChangeLog,
	saveProductMeasurementBulkModifiedSizes,
	saveProductMeasurementChangeLog
} from '@store/actions/MeasurementAction';
import { Types } from '@store/types';

import { fetchEnumValues } from '@services/commonService';
import { removeSizeCategorySequence } from '@services/measurementService';
import { getAllPomResponse } from '@services/pomService';

import { isAdmin, validateFloatNumber } from '@utils/Util';

const CostingAndPPSampleTable = ({
	isMeasurementEditable,
	selectedMeasurementType,
	sizeCategorySequence = {},
	fitWiseSizeList = {},
	tableData = [],
	isShowSlideIcon = false,
	hasShowActualAndDiffColumn = false,
	parentComponent,
	onSizeConvert,
	isOpenSizeConvertDropdwon,
	setOpenSizeConvertDropdown,
	onHideSizeColumn,
	hiddenColumnIds = [],
	onShowAllHiddenSizeColumn,
	onDuplicateMeasurement,
	onReplacePOM,
	showReplaceDropdownId,
	setShowReplaceDropdownId,
	pomAndSizeList
}) => {
	const [showGradingDropDown, setShowGradingDropDown] = useState(false);
	const [tmpSelectedGroupGrade, setTmpSelectedGroupGrade] = useState({});
	const [selectedGroupGrade, setSelectedGroupGrade] = useState({});
	const [selectedGradingId, setSelectedGradingId] = useState(null);
	const [selectedSizeId, setSelectedSizeId] = useState(null);
	const [arrowDisable, setArrowDisable] = useState(true);
	const [gradingTypes, setGradingTypes] = useState([]);
	const [selectedRow, setSelectedRow] = useState({});
	const [allPomList, setAllPomList] = useState([]);
	const [selectedPom, setSelectedPom] = useState(null);
	const [gradings, setGradings] = useState({});
	const gradingDropDownOptionRef = useRef(null);
	const gradingDropDownRef = useRef(null);
	const sizeDropDownRef = useRef(null);
	const elementRef = useRef(null);
	const dispatch = useDispatch();
	const { id } = useParams();

	const selectedSizeCategoryList =
		useSelector((store) => store.design.pomAndSize?.sizeCategory?.productSizeList) || [];

	useEffect(() => {
		onFetchPomList();
	}, []);

	const onFetchPomList = async () => {
		try {
			dispatch(openLoader());
			const data = await getAllPomResponse();
			setAllPomList(data);
			dispatch(closeLoader());
		} catch (error) {
			dispatch(closeLoader());
			toastError(error.message);
		}
	};

	let allPoms = allPomList?.map((item) => {
		return {
			id: item.id,
			referenceId: item.referenceId,
			value: `${item.pomCode}: ${item.name}`
		};
	});

	const getAllPomOptions = () => {
		let allPomOptionsList = allPoms || [];
		let selectedPoms = tableData
			?.map((item) => item?.pomResponse?.referenceId)
			?.filter((referenceId) => referenceId);
		return allPomOptionsList?.filter((pom) => !selectedPoms.includes(pom?.referenceId));
	};

	const onSetMeasurementSizes = async () => {
		try {
			dispatch(openLoader());
			await dispatch(fetchProductWiseMeasurementSizeByCategoryId(pomAndSizeList.sizeCategoryId));
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		if (pomAndSizeList?.sizeCategoryId) {
			(async () => {
				await onSetMeasurementSizes();
			})();
		}
	}, [pomAndSizeList?.sizeCategoryId]);

	useEffect(() => {
		(async () => {
			try {
				dispatch(openLoader());
				const data = await fetchEnumValues('info.nitex.app.measurement.GradingType');
				setGradingTypes(data);
				dispatch(closeLoader());
			} catch {
				dispatch(closeLoader());
			}
		})();
		document.addEventListener('mousedown', handleOutsideClick);
		return () => {
			document.removeEventListener('mousedown', handleOutsideClick);
		};
	}, []);

	const handleOutsideClick = (event) => {
		if (sizeDropDownRef && !sizeDropDownRef.current?.contains(event.target)) {
			setSelectedSizeId(null);

			if (setOpenSizeConvertDropdown) setOpenSizeConvertDropdown(false);
		}
		if (gradingDropDownOptionRef && !gradingDropDownOptionRef.current?.contains(event.target)) {
			setSelectedGradingId(null);
		}
		if (gradingDropDownRef && !gradingDropDownRef.current?.contains(event.target)) {
			setShowGradingDropDown(false);
		}
	};

	useEffect(() => {
		if (fitWiseSizeList?.FIRST_FIT) {
			let sortedSizes = sortBySerial(fitWiseSizeList?.FIRST_FIT?.sizeList);
			let groupGradingBaseSize = sortedSizes?.find(
				(item) => item.id === fitWiseSizeList?.FIRST_FIT?.groupGradingBaseSize
			);
			if (groupGradingBaseSize) {
				setSelectedGroupGrade(groupGradingBaseSize);
				setTmpSelectedGroupGrade(groupGradingBaseSize);
			}
		}
	}, [fitWiseSizeList]);

	useEffect(() => {
		if (fitWiseSizeList?.FIRST_FIT) {
			let sortedSizes = sortBySerial(fitWiseSizeList?.FIRST_FIT?.sizeList);
			let baseSizeIndex = sortedSizes?.findIndex(
				(item) => item.id === fitWiseSizeList?.FIRST_FIT?.baseSize
			);
			let grading1 = [];
			let tmpGrading2 = [];
			if (baseSizeIndex !== -1) {
				grading1 = sortedSizes.slice(0, baseSizeIndex);
				tmpGrading2 = sortedSizes.slice(baseSizeIndex + 1);
			}

			let grading2 = [];
			let grading3 = [];
			if (selectedGroupGrade?.id) {
				const groupGradeBaseSize = tmpGrading2?.findIndex(
					(item) => item.id === selectedGroupGrade.id
				);
				grading2 = tmpGrading2.slice(0, groupGradeBaseSize + 1);
				grading3 = tmpGrading2.slice(groupGradeBaseSize + 1);
			} else {
				grading2 = tmpGrading2;
			}

			setGradings({
				grading1,
				grading2,
				grading3
			});
		}
	}, [fitWiseSizeList, selectedGroupGrade]);

	const isEditable = () => {
		return isMeasurementEditable;
	};

	const isBaseSizeExist = () => {
		return tableData?.[0]?.fitInfo['FIRST_FIT']?.baseSize;
	};

	const isBaseSizeValueExist = (obj) => {
		if (obj?.baseSize) {
			let selectedSize = obj?.sizeValueList?.find((item) => item.id === obj.baseSize);
			return !!selectedSize?.value;
		} else {
			return false;
		}
	};

	const isGroupGradeBaseSizeValueExist = (obj) => {
		let groupBaseSize = fitWiseSizeList?.FIRST_FIT?.groupGradingBaseSize;
		if (groupBaseSize) {
			let selectedSize = obj?.sizeValueList?.find((item) => item.id === groupBaseSize);
			return !!selectedSize?.value;
		} else {
			return false;
		}
	};

	const changeBaseSize = async (sizeId) => {
		if (isEditable()) {
			// let fitId =
			// 	(tableData && tableData.length > 0 && tableData[0]?.fitInfo['FIRST_FIT']?.fitTypeId) || null;
			let fitId = fitWiseSizeList['FIRST_FIT']?.fitTypeId || null;
			let changeLogInfo = {
				changeLogType: 'BASE_SIZE_UPDATE',
				id: fitId,
				dto: {
					sizeId: sizeId
				}
			};
			await dispatch(saveProductMeasurementChangeLog(changeLogInfo));
			await dispatch(changeCostingPpSampleBaseSize({ baseSize: sizeId }));
			setSelectedSizeId(null);
		}
	};

	const copyGradings = (gradingId) => {
		if (tableData?.length > 0) {
			let selectedGradingValues = [];
			for (let item of tableData) {
				let gradingValue = item.fitInfo['FIRST_FIT'][gradingId];
				selectedGradingValues.push(gradingValue);
			}

			if (selectedGradingValues.length > 0) {
				localStorage.setItem('MEASUREMENT_SIZE_VALUES', JSON.stringify(selectedGradingValues));
				toast.success('Grading added in clipboard!');
			}
			setSelectedGradingId(null);
		}
	};

	const copyColumn = (sizeId) => {
		if (tableData?.length > 0) {
			let selectedSizeValues = [];
			for (let item of tableData) {
				let sizeList = item.fitInfo['FIRST_FIT'].sizeValueList;
				for (let size of sizeList) {
					if (size.id === sizeId) {
						selectedSizeValues.push(size.value);
					}
				}
			}

			if (selectedSizeValues.length > 0) {
				localStorage.setItem('MEASUREMENT_SIZE_VALUES', JSON.stringify(selectedSizeValues));
				toast.success('Size added in clipboard!');
			}
			setSelectedSizeId(null);
		}
	};

	const pasteGradings = async (gradingId) => {
		if (isEditable()) {
			let gradingValues = localStorage.getItem('MEASUREMENT_SIZE_VALUES');
			if (gradingValues) {
				gradingValues = JSON.parse(gradingValues);
				gradingValues.forEach((grade, index) => {
					let emptyEvent = { preventDefault: () => false, target: { value: grade } };
					let item = tableData[index];
					let obj = item.fitInfo['FIRST_FIT'];
					handleChange(emptyEvent, obj.pomResponseId, gradingId, 'FIRST_FIT');
					onChangeGrading(emptyEvent, obj, gradingId === 'grading1' ? 'FIRST' : 'SECOND');
				});
				setSelectedGradingId(null);
			}
		}
	};

	const pasteColumn = async (sizeId) => {
		if (isEditable()) {
			let sizeValues = localStorage.getItem('MEASUREMENT_SIZE_VALUES');
			if (sizeValues) {
				sizeValues = JSON.parse(sizeValues);
				if (sizeValues.length > 0) {
					let bulkChangeLogs = [];
					let modifiedSizeList = {};
					let index = 0;
					for (let size of sizeValues) {
						if (index < tableData.length) {
							let item = tableData[index];
							let obj = item.fitInfo['FIRST_FIT'];
							let changeLogInfo = {
								changeLogType: 'MEASUREMENT_VALUE_UPDATE',
								id: obj.fitTypeId,
								dto: {
									pointOfMeasurementId: obj.pomResponseId,
									value: size ? size : null,
									sizeId: sizeId
								}
							};
							modifiedSizeList[obj.pomResponseId] = size;
							bulkChangeLogs.push(changeLogInfo);
							index++;
						} else {
							break;
						}
					}
					await dispatch(saveProductMeasurementBulkChangeLog(bulkChangeLogs));
					await dispatch(
						pasteMeasurementSizeValues({ sizeId, modifiedSizeList, currentFit: 'FIRST_FIT' })
					);
					setSelectedSizeId(null);
				}
			}
		}
	};

	const clearColumn = async (sizeId) => {
		if (isEditable()) {
			let bulkChangeLogs = [];
			let modifiedSizeList = {};
			for (let item of tableData) {
				let sizeList = item.fitInfo['FIRST_FIT'].sizeValueList;
				for (let size of sizeList) {
					if (size.id === sizeId) {
						let obj = item.fitInfo['FIRST_FIT'];
						let changeLogInfo = {
							changeLogType: 'MEASUREMENT_VALUE_UPDATE',
							id: obj.fitTypeId,
							dto: {
								pointOfMeasurementId: obj.pomResponseId,
								value: null,
								sizeId: sizeId
							}
						};
						modifiedSizeList[obj.pomResponseId] = '';
						bulkChangeLogs.push(changeLogInfo);
					}
				}
			}
			await dispatch(saveProductMeasurementBulkChangeLog(bulkChangeLogs));
			await dispatch(pasteMeasurementSizeValues({ sizeId, modifiedSizeList, currentFit: 'FIRST_FIT' }));
			setSelectedSizeId(null);
		}
	};

	const isSameSizeCategory = (selectedSizeCategoryId) => {
		const selectedSizeCategories = [...selectedSizeCategoryList];
		const sizeCategoryIds = [];
		selectedSizeCategories.forEach((category) => {
			if (category.sizeCategoryId) sizeCategoryIds.push(category.sizeCategoryId);
		});
		return sizeCategoryIds.every((id) => id === selectedSizeCategoryId);
	};

	const removeColumn = async (sizeId, sizeCategoryId) => {
		if (isEditable()) {
			let sameSizeCategoryList = fitWiseSizeList?.['FIRST_FIT']?.sizeList?.filter(
				(item) => item.sizeCategoryId === sizeCategoryId
			);
			const removeSize = async () => {
				let fitId = fitWiseSizeList['FIRST_FIT']?.fitTypeId;
				let changeLogInfo = {
					changeLogType: 'SIZE_REMOVE',
					id: fitId,
					dto: {
						sizeId: sizeId
					}
				};
				await dispatch(saveProductMeasurementChangeLog(changeLogInfo));
				await dispatch(removeMeasurementSize({ sizeId }));
				setSelectedSizeId(null);
			};
			if (sameSizeCategoryList?.length === 1) {
				try {
					dispatch(openLoader());
					const data = await removeSizeCategorySequence(
						id,
						sizeCategoryId,
						selectedMeasurementType.constant
					);
					dispatch({
						type: Types.ADD_MEASUREMENT_SIZE_CATEGORY_SEQUENCE,
						payload: data
					});
					await removeSize();
					dispatch(closeLoader());
				} catch {
					dispatch(closeLoader());
				}
			} else {
				await removeSize();
			}
		}
	};

	const renderSizeDropDown = (sizeId, sizeCategoryId, item, baseSize, sizes) => {
		if (selectedSizeId === sizeId) {
			return (
				<div className="dropdown-menu show action-heading-dropdown" ref={sizeDropDownRef}>
					<ul>
						{isEditable() && (
							<li onClick={() => changeBaseSize(sizeId)}>
								<span>Mark as base size</span>
							</li>
						)}
						<li onClick={() => copyColumn(sizeId)}>
							<span>Copy column</span>
						</li>
						{isEditable() &&
							parentComponent !== 'TECH_PACK' &&
							!isSameSizeCategory(sizeCategoryId, item) && (
								<li className="position-relative">
									<span
										className="d-flex justify-content-between align-items-center"
										onClick={() => setOpenSizeConvertDropdown((prev) => !prev)}
									>
										Size convert to{' '}
										<img
											src={`${process.env.PUBLIC_URL}/icons/right-arrow.svg`}
											alt="arror"
										/>
									</span>

									{isOpenSizeConvertDropdwon && (
										<div className="dropdown-menu size-convert-dropdown show scroll-y-label">
											<ul>
												{selectedSizeCategoryList &&
													selectedSizeCategoryList.length > 0 &&
													selectedSizeCategoryList
														.filter(
															(sizeItem) =>
																!sizes.some((size) => size.id === sizeItem.id)
														)
														.map((category) => (
															<li
																key={category?.id}
																onClick={() => onSizeConvert(category, item)}
															>
																<span>{category?.label}</span>
															</li>
														))}
											</ul>
										</div>
									)}
								</li>
							)}

						{isEditable() && (
							<>
								<li onClick={() => pasteColumn(sizeId)}>
									<span>Paste column</span>
								</li>
								<li onClick={() => clearColumn(sizeId)}>
									<span>Clear column</span>
								</li>
								{baseSize !== sizeId && (
									<li onClick={() => onHideSizeColumn(sizeId)}>
										<span>Hide column</span>
									</li>
								)}

								<li onClick={() => removeColumn(sizeId, sizeCategoryId)}>
									<span className="red">Remove column</span>
								</li>
							</>
						)}
					</ul>
				</div>
			);
		}
	};

	const onRemoveGroupGrading = async () => {
		if (isEditable()) {
			let fitId = fitWiseSizeList['FIRST_FIT']?.fitTypeId;
			let changeLogInfo = {
				changeLogType: 'GRADING_BASE_SIZE_UPDATE',
				id: fitId,
				dto: {
					// sizeId: selectedGroupGrade.id
					sizeId: null
				}
			};
			await dispatch(saveProductMeasurementChangeLog(changeLogInfo));
			await dispatch(removeMeasurementGroupGrading());
			setSelectedGroupGrade({});
			setTmpSelectedGroupGrade({});
			setShowGradingDropDown(false);
		}
	};

	const onAddGroupGrading = async (groupGrade) => {
		if (isEditable()) {
			let fitId = tableData[0]?.fitInfo['FIRST_FIT']?.fitTypeId;
			let changeLogInfo = {
				changeLogType: 'GRADING_BASE_SIZE_UPDATE',
				id: fitId,
				dto: {
					sizeId: groupGrade.id
				}
			};
			await dispatch(saveProductMeasurementChangeLog(changeLogInfo));
			await dispatch(addMeasurementGroupGrading({ groupGradingBaseSize: groupGrade.id }));
		}
	};

	const renderGradingDropDown = () => {
		if (showGradingDropDown) {
			const baseSizeInfo = fitWiseSizeList['FIRST_FIT']?.sizeList?.find(
				(item) => item.id === fitWiseSizeList.FIRST_FIT.baseSize
			);
			const lastSize = gradings['grading2'][gradings['grading2'].length - 1];
			return (
				<div className="group-grading-popup" ref={gradingDropDownRef}>
					<p className="regular-14 gray_dark_02 mb-3">Base size: {baseSizeInfo?.label}</p>
					<div className="dropdown">
						<p className="regular-14 gray_dark_02 mb-1">Select middle size</p>
						<div className="select-component">
							<div
								className="click-box click mb-1"
								data-toggle="dropdown"
								aria-haspopup="true"
								aria-expanded="false"
							>
								<p className="regular-14 gray_dark_03">{tmpSelectedGroupGrade?.label}</p>
								<img src={`${process.env.PUBLIC_URL}/images/chevron_down.svg`} alt="" />
							</div>
							<div className="dropdown-box dropdown-menu">
								<div className="list-items scroll-y-label">
									{gradings['grading2'].concat(gradings['grading3'])?.map((item) => {
										if (item?.id !== lastSize?.id) {
											return (
												<div
													className="single-item"
													key={`grading_2_size_${item.id}`}
													onClick={() => setTmpSelectedGroupGrade(item)}
												>
													<p>{item.label}</p>
												</div>
											);
										}
									})}
								</div>
							</div>
						</div>
					</div>
					<p className="regular-14 gray_dark_02 mt-3 mb-2">Last size: {lastSize?.label}</p>
					<div className="buttons d-flex justify-content-between">
						<div>
							{selectedGroupGrade?.label && (
								<button
									className="button text-only size32"
									onClick={async (e) => {
										e.stopPropagation();
										await onRemoveGroupGrading();
									}}
								>
									<span className="red">DELETE</span>
								</button>
							)}
						</div>

						<div>
							<button
								className="button text-only size32"
								onClick={(e) => {
									e.stopPropagation();
									setTmpSelectedGroupGrade({});
									setShowGradingDropDown(false);
								}}
							>
								<span className="gray_dark_02">CANCEL</span>
							</button>
							<button
								className="button text-only size32 ml-2"
								onClick={async (e) => {
									e.stopPropagation();
									await onAddGroupGrading(tmpSelectedGroupGrade);
									setSelectedGroupGrade(tmpSelectedGroupGrade);
									setShowGradingDropDown(false);
								}}
							>
								<span>SUBMIT</span>
							</button>
						</div>
					</div>
				</div>
			);
		}
	};

	const handleDragStart = (e, index) => {
		e.dataTransfer.setData('text/plain', index);
	};

	const handleDragOver = (e) => {
		e.preventDefault();
	};

	const handleDrop = (e, endIndex) => {
		if (isEditable()) {
			e.preventDefault();
			const startIndex = e.dataTransfer.getData('text/plain');
			if (startIndex >= 0 && endIndex >= 0) {
				dispatch(
					reArrangeMeasurementTable({
						destinationIndex: endIndex,
						sourceIndex: startIndex,
						fitId: fitWiseSizeList['FIRST_FIT']?.fitTypeId
					})
				);
			}
		}
	};

	const generateHeadingColspan = () => {
		let isBaseSize = !!fitWiseSizeList['FIRST_FIT']?.baseSize;
		let sum = 8;
		if (fitWiseSizeList['FIRST_FIT']?.sizeList?.length) {
			sum += fitWiseSizeList['FIRST_FIT']?.sizeList.length;
		}
		if (isBaseSize && selectedMeasurementType.constant !== 'COSTING_GRADING') {
			sum += 2;
		}
		return sum;
	};

	const renderSizeHeading = () => {
		const renderCommonHeading = () => {
			let isSizeExist = fitWiseSizeList['FIRST_FIT']?.sizeList.length > 0;
			if (
				isSizeExist &&
				selectedMeasurementType.constant !== 'COSTING_GRADING' &&
				hasShowActualAndDiffColumn
			) {
				return (
					<>
						<th>
							<span className="sizename">Actual</span>
						</th>
						<th>
							<span className="sizename">Difference</span>
						</th>
					</>
				);
			}
		};

		if (Object.keys(fitWiseSizeList).length > 0) {
			const renderSizeList = (item) => {
				const sizes = sortBySerial(item.sizeList);
				// const sizes = item.sizeList;
				const baseSize = item.baseSize;
				return sizes?.map((item, index) => {
					return (
						<React.Fragment key={`table_th_size_${index}`}>
							{!hiddenColumnIds.includes(item.id) && (
								<th className="cursor-pointer">
									<div
										onClick={() => {
											setSelectedSizeId(item.id);
										}}
									>
										<span className="sizename">{item.label}</span>
										{item.id === baseSize && <p>(Target)</p>}
									</div>
									{renderSizeDropDown(item.id, item.sizeCategoryId, item, baseSize, sizes)}
								</th>
							)}
							{item.id === baseSize && renderCommonHeading()}
						</React.Fragment>
					);
				});
			};

			return Object.keys(fitWiseSizeList)?.map((item, index) => {
				return (
					<React.Fragment key={`table_th_${index}`}>
						{renderSizeList(fitWiseSizeList[item])}
					</React.Fragment>
				);
			});
		} else {
			return <>{renderCommonHeading()}</>;
		}
	};

	const handleSelectedRow = (pomResponseId = null, fieldType = '', fitType = '', defaultValue) => {
		if (isEditable()) {
			setSelectedRow({
				pomResponseId: pomResponseId,
				fieldType: fieldType,
				fitType: fitType,
				defaultValue
			});
		}
	};
	const onPomRemove = (item) => {
		if (isEditable()) {
			let changeLogInfo = {
				id: fitWiseSizeList['FIRST_FIT']?.fitTypeId,
				changeLogType: 'POINT_REMOVE',
				dto: {
					pointOfMeasurementId: item.libraryPOMId || item?.id
				}
			};
			dispatch(saveProductMeasurementChangeLog(changeLogInfo));
			dispatch(removePointInMeasurementTable(item?.id));
		}
	};

	const handleSizeChange = (e, pomResponseId, fieldName, fitType, existingSizeList, sizeId) => {
		e.preventDefault();
		if (isEditable()) {
			dispatch(
				modifyMeasurementTableCellValue({
					pointId: pomResponseId,
					fieldName,
					fitType,
					value: existingSizeList?.map((item) => {
						if (item.id === sizeId) {
							item.value = e.target.value;
						}
						return item;
					})
				})
			);
		}
	};

	const onUpdateSize = (e, pointId, logType, sizeId, fitTypeId) => {
		e.preventDefault();
		let newValue = e.target?.value ? Number(e.target.value) : '';
		if (isEditable() && selectedRow?.defaultValue !== newValue) {
			let changeLogInfo = {
				changeLogType: logType,
				id: fitTypeId,
				dto: {
					pointOfMeasurementId: pointId,
					value: e.target.value || null,
					sizeId: sizeId
				}
			};
			dispatch(saveProductMeasurementChangeLog(changeLogInfo));
		}
	};

	const handleChange = (e, pomResponseId, fieldName, fitType) => {
		e.preventDefault();
		if (isEditable()) {
			dispatch(
				modifyMeasurementTableCellValue({
					pointId: pomResponseId,
					fieldName,
					fitType,
					value: e.target.value || ''
				})
			);
		}
	};

	const updateToleranceOrActualValue = async (e, pointId, logType, fitTypeId) => {
		let newValue = e.target?.value ? Number(e.target.value) : '';
		if (isEditable() && selectedRow.defaultValue !== newValue) {
			let changeLogInfo = {
				changeLogType: logType,
				id: fitTypeId,
				dto: {
					pointOfMeasurementId: pointId,
					value: e.target.value || null
				}
			};
			await dispatch(saveProductMeasurementChangeLog(changeLogInfo));
		}
	};

	const onBlurKey = async (event, obj, logType, item) => {
		event.preventDefault();
		let newValue = event.target?.value ? Number(event.target.value) : '';
		if (isEditable() && selectedRow?.defaultValue !== newValue) {
			let changeLogInfo = {
				changeLogType: 'MEASUREMENT_VALUE_UPDATE',
				id: obj.fitTypeId,
				dto: {
					pointOfMeasurementId: obj.pomResponseId,
					value: event.target.value || null,
					sizeId: item.id
				}
			};
			dispatch(saveProductMeasurementChangeLog(changeLogInfo));
		}

		if (!event.key) {
			if (obj.gradingType.constant === 'PARALLEL_PROPORTIONAL') {
				targetValueChanges(event, item, obj);
			}
			if (obj.gradingType.constant === 'PEER') {
				targetValueChangesPeerGrading(event, item, obj);
				//await onChangePeerGrading(value, obj, gradingNumber);
			}
			if (obj.gradingType.constant === 'GROUP') {
				//await onChangeGroupGrading(value, obj, gradingNumber);
				targetValueChangesGroupGrading(event, item, obj);
			}
		}
	};
	const onPressKey = async (event, obj, fitType = 'FIRST_FIT', logType, item) => {
		const isUp = event.key === 'ArrowUp';
		const isDown = event.key === 'ArrowDown';
		const isTab = event.key === 'Tab';
		const isEnter = event.key === 'Enter';

		if (!(isUp || isDown || isTab || isEnter)) return;
		event.preventDefault();
		let pomResponseId = obj?.pomResponseId;
		if (!pomResponseId) return;
		let { name } = event.target;

		let focusAblePomId = null;

		if (isUp || isDown || isEnter) {
			let currentIndex = tableData.findIndex((item) => item.pomResponse.id === pomResponseId);
			if (isUp && currentIndex > 0) {
				focusAblePomId = tableData[currentIndex - 1].pomResponse.id;
			}
			if ((isDown || isEnter) && currentIndex < tableData.length - 1) {
				focusAblePomId = tableData[currentIndex + 1].pomResponse.id;
			}
		}

		if (isTab) {
			focusAblePomId = pomResponseId;
		}

		let defaultFieldType = name;
		if (isTab) {
			let fieldList = ['pomName', 'tolerance'];
			let currentFitSizeList = fitWiseSizeList[fitType];
			let index = 0;
			for (let item of currentFitSizeList.sizeList) {
				fieldList.push(`sizeValue_${index}`);
				if (currentFitSizeList.baseSize && currentFitSizeList.baseSize === item.id) {
					fieldList.push('actualValue');
				}
				index++;
			}
			fieldList = [...fieldList, ...['remarks', 'grading1', 'grading2', 'grading3']];
			let selectedIndex = fieldList.findIndex((item) => item === name);

			defaultFieldType =
				selectedIndex < fieldList.length - 1 ? fieldList[selectedIndex + 1] : fieldList[0];

			if (selectedIndex > 0 && defaultFieldType === fieldList[0]) {
				let currentIndex = tableData.findIndex((item) => item.pomResponse.id === pomResponseId);
				let tableLength = tableData.length - 1;
				if (currentIndex < tableLength && tableLength !== currentIndex) {
					focusAblePomId = tableData[currentIndex + 1].pomResponse.id;
				} else {
					focusAblePomId = null;
					defaultFieldType = '';
				}
			}
		}

		if (focusAblePomId) {
			setSelectedRow({
				pomResponseId: focusAblePomId,
				fieldType: defaultFieldType,
				fitType
			});
			if (logType === 'MEASUREMENT_VALUE_UPDATE') {
				onUpdateSize(event, obj.pomResponseId, 'MEASUREMENT_VALUE_UPDATE', item.id, obj.fitTypeId);
			}
			if (name === 'actualValue') {
				await updateToleranceOrActualValue(
					event,
					obj.pomResponseId,
					'ACTUAL_VALUE_UPDATE',
					obj.fitTypeId
				);
			} else if (name === 'grading1' && event?.target?.value) {
				await onChangeGrading(event, obj, 'FIRST');
			} else if (name === 'grading2' && event?.target?.value) {
				await onChangeGrading(event, obj, 'SECOND');
			} else if (name === 'grading3' && event?.target?.value) {
				await onChangeGrading(event, obj, 'THIRD');
			} else if (name === 'tolerance') {
				await updateToleranceOrActualValue(
					event,
					obj.pomResponseId,
					'TOLERANCE_UPDATE',
					obj.fitTypeId
				);
			} else if (name === 'remarks') {
				await handleOnBlurChangeLog(
					event,
					'POINT_REMARKS_UPDATE',
					'remarks',
					obj.pomResponseId,
					obj.fitTypeId
				);
			} else if (name === 'pomName') {
				await handleOnBlurChangeLog(
					event,
					'POINT_NAME_UPDATE',
					'name',
					obj.pomResponseId,
					obj.fitTypeId
				);
			}
		}
		if (obj.gradingType.constant === 'PARALLEL_PROPORTIONAL') {
			targetValueChanges(event, item, obj);
		}
		if (obj.gradingType.constant === 'PEER') {
			targetValueChangesPeerGrading(event, item, obj);
			//await onChangePeerGrading(value, obj, gradingNumber);
		}
		if (obj.gradingType.constant === 'GROUP') {
			//await onChangeGroupGrading(value, obj, gradingNumber);
			targetValueChangesGroupGrading(event, item, obj);
		}
	};
	const targetValueChangesGroupGrading = async (event, item, obj) => {
		event.preventDefault();
		let input2 = event?.target?.value;
		const {
			grade: grade1,
			baseSize: baseSize1,
			existingSizeList: existingSizeList1
		} = getGradingInfos('FIRST', obj);
		const { grade: grade2, existingSizeList: existingSizeList2 } = getGradingInfos('SECOND', obj);

		const {
			grade: grade3,
			baseSize: baseSize3,
			existingSizeList: existingSizeList3
		} = getGradingInfos('THIRD', obj);
		if (item && obj && item.id === obj.baseSize && obj.grading1) {
			commonFunctionGroupGrading(grade1, existingSizeList1, baseSize1, obj, input2, obj.grading1);
		}
		if (item && obj && item.id === obj.baseSize && obj.grading2) {
			if (grade2 === 2) {
				let bulkChangeLogs = [];
				let modifiedSizeList = [];
				const reversedList = existingSizeList2;
				for (let item of reversedList) {
					let input = input2 ? Number(input2) : 0;
					let newValue = Number(input || 0) + obj.grading2;
					let changeLogInfo = {
						changeLogType: 'MEASUREMENT_VALUE_UPDATE',
						id: obj.fitTypeId,
						dto: {
							pointOfMeasurementId: obj.pomResponseId,
							value: newValue || null,
							sizeId: item.id
						}
					};
					bulkChangeLogs.push(changeLogInfo);
					modifiedSizeList.push({ ...item, value: newValue });
				}
				await saveBulkSizeChanges(obj, bulkChangeLogs, modifiedSizeList);
				// commonFunctionGroupGrading(grade2, existingSizeList2, baseSize2, obj, input2, obj.grading2);
				if (grade3 === 3 && existingSizeList3.length > 0 && obj.grading2 && obj.grading3) {
					let bulkChangeLogs = [];
					let modifiedSizeList = [];

					const reversedList = existingSizeList3;
					for (let item of reversedList) {
						//let input = input2 ? Number(input2) : 0;
						let newValue = baseSize3.value + obj.grading3;
						let changeLogInfo = {
							changeLogType: 'MEASUREMENT_VALUE_UPDATE',
							id: obj.fitTypeId,
							dto: {
								pointOfMeasurementId: obj.pomResponseId,
								value: newValue || null,
								sizeId: item.id
							}
						};
						bulkChangeLogs.push(changeLogInfo);
						modifiedSizeList.push({ ...item, value: newValue });
					}
					await saveBulkSizeChanges(obj, bulkChangeLogs, modifiedSizeList);
				}
			}
		}
		if (item && obj && obj.grading3 && baseSize3.id === item.id) {
			commonFunctionGroupGrading(grade3, existingSizeList3, baseSize3, obj, input2, obj.grading3);
		}
	};
	const commonFunctionGroupGrading = async (
		grade,
		existingSizeList,
		baseSize,
		obj,
		inputValue,
		grading
	) => {
		let bulkChangeLogs = [];
		let modifiedSizeList = [];
		const reversedList = grade === 1 ? existingSizeList.reverse() : existingSizeList;
		for (let item of reversedList) {
			let input = inputValue ? Number(inputValue) : 0;

			let newValue = grade === 1 ? Number(input || 0) - grading : Number(input || 0) + grading;
			let changeLogInfo = {
				changeLogType: 'MEASUREMENT_VALUE_UPDATE',
				id: obj.fitTypeId,
				dto: {
					pointOfMeasurementId: obj.pomResponseId,
					value: newValue || null,
					sizeId: item.id
				}
			};
			bulkChangeLogs.push(changeLogInfo);
			modifiedSizeList.push({ ...item, value: newValue });
		}
		await saveBulkSizeChanges(obj, bulkChangeLogs, modifiedSizeList);
	};
	const commonFunction = async (grade, existingSizeList, baseSize, obj, inputValue, grading) => {
		let bulkChangeLogs = [];
		let modifiedSizeList = [];
		const reversedList = grade === 1 ? existingSizeList.reverse() : existingSizeList;
		let count = 1;
		const addChangeLog = (newValue, sizeId) => {
			return {
				changeLogType: 'MEASUREMENT_VALUE_UPDATE',
				id: obj.fitTypeId,
				dto: {
					pointOfMeasurementId: obj.pomResponseId,
					value: newValue ? newValue : null,
					sizeId: sizeId
				}
			};
		};
		for (let i = 0; i < reversedList.length; i++) {
			let firstPeer = reversedList[i];
			let secondPeer = (grade === 2 || grade === 3) && i === 0 ? {} : reversedList[i + 1];
			let input = inputValue ? Number(inputValue) : 0;
			let newValue =
				grade === 1
					? Number(input || 0) - obj.grading1 * count
					: Number(input || 0) + grading * count;

			if (firstPeer?.id) {
				let modifiedValue = (grade === 2 || grade === 3) && i === 0 ? baseSize?.value || 0 : newValue;
				bulkChangeLogs.push(addChangeLog(modifiedValue, firstPeer.id));
				modifiedSizeList.push({
					...firstPeer,
					value: modifiedValue
				});
			}

			if (secondPeer?.id) {
				bulkChangeLogs.push(addChangeLog(newValue, secondPeer.id));
				modifiedSizeList.push({ ...secondPeer, value: newValue });
			}
			if (!((grade === 2 || grade === 3) && i === 0)) {
				i++;
				count++;
			}
		}

		await saveBulkSizeChanges(obj, bulkChangeLogs, modifiedSizeList);
	};
	const targetValueChangesPeerGrading = async (event, item, obj) => {
		event.preventDefault();
		let input2 = event?.target?.value;
		const {
			grade: grade1,
			baseSize: baseSize1,
			existingSizeList: existingSizeList1
		} = getGradingInfos('FIRST', obj);
		const {
			grade: grade2,
			baseSize: baseSize2,
			existingSizeList: existingSizeList2
		} = getGradingInfos('SECOND', obj);

		const {
			grade: grade3,
			baseSize: baseSize3,
			existingSizeList: existingSizeList3
		} = getGradingInfos('THIRD', obj);
		if (item && obj && item.id === obj.baseSize && obj.grading1) {
			commonFunction(grade1, existingSizeList1, baseSize1, obj, input2, obj.grading1);
		}
		if (item && obj && item.id === obj.baseSize && obj.grading2) {
			if (grade2 === 2) {
				let bulkChangeLogs = [];
				let modifiedSizeList = [];
				const reversedList = existingSizeList2;
				let count = 1;
				const addChangeLog = (newValue, sizeId) => {
					return {
						changeLogType: 'MEASUREMENT_VALUE_UPDATE',
						id: obj.fitTypeId,
						dto: {
							pointOfMeasurementId: obj.pomResponseId,
							value: newValue ? newValue : null,
							sizeId: sizeId
						}
					};
				};
				for (let i = 0; i < reversedList.length; i++) {
					let firstPeer = reversedList[i];
					let secondPeer = (grade2 === 2 || grade3 === 3) && i === 0 ? {} : reversedList[i + 1];
					let input = input2 ? Number(input2) : 0;
					let newValue = Number(input || 0) + Number(obj.grading2) * count;

					if (firstPeer?.id) {
						let modifiedValue =
							(grade2 === 2 || grade3 === 3) && i === 0 ? baseSize2?.value || 0 : newValue;
						bulkChangeLogs.push(addChangeLog(baseSize2?.value, firstPeer.id));
						modifiedSizeList.push({
							...firstPeer,
							value: modifiedValue
						});
					}

					if (secondPeer?.id) {
						bulkChangeLogs.push(addChangeLog(newValue, secondPeer.id));
						modifiedSizeList.push({ ...secondPeer, value: newValue });
					}
					if (!((grade2 === 2 || grade3 === 3) && i === 0)) {
						i++;
						count++;
					}
				}
				await saveBulkSizeChanges(obj, bulkChangeLogs, modifiedSizeList);
				let count3 = 1;
				if (grade3 === 3 && existingSizeList3.length > 0 && obj.grading2 && obj.grading3) {
					//let kkk = Number(input2 || 0) + Number(obj.grading2 * (count - 1));
					for (let i = 0; i < existingSizeList3.length; i++) {
						let firstPeer = existingSizeList3[i];

						let secondPeer =
							(grade2 === 2 || grade3 === 3) && i === 0 ? {} : existingSizeList3[i + 1];

						let newValue =
							Number(input2 || 0) +
							Number(obj.grading2) * count +
							+Number(obj.grading3) * count3 -
							Number(obj.grading2) * count3;

						if (firstPeer?.id) {
							let modifiedValue =
								(grade2 === 2 || grade3 === 3) && i === 0 ? baseSize3?.value || 0 : newValue;
							bulkChangeLogs.push(addChangeLog(modifiedValue, firstPeer.id));
							modifiedSizeList.push({
								...firstPeer,
								value: modifiedValue
							});
						}

						if (secondPeer?.id) {
							bulkChangeLogs.push(addChangeLog(newValue, secondPeer.id));
							modifiedSizeList.push({ ...secondPeer, value: newValue });
						}
						if (!((grade2 === 2 || grade3 === 3) && i === 0)) {
							i++;
							count++;
							count3++;
						}
					}
					await saveBulkSizeChanges(obj, bulkChangeLogs, modifiedSizeList);
				}
			}
		}
		if (item && obj && obj.grading3 && baseSize3.id === item.id) {
			commonFunction(grade3, existingSizeList3, baseSize3, obj, input2, obj.grading3);
		}
	};

	const targetValueChanges = async (event, item, obj) => {
		event.preventDefault();
		let input2 = event?.target?.value;
		const { grade: grade1, existingSizeList: existingSizeList1 } = getGradingInfos('FIRST', obj);
		const { grade: grade2, existingSizeList: existingSizeList2 } = getGradingInfos('SECOND', obj);

		const {
			grade: grate3,
			baseSize: baseSize3,
			existingSizeList: existingSizeList3
		} = getGradingInfos('THIRD', obj);
		if (item && obj && item.id === obj.baseSize && obj.grading1) {
			let bulkChangeLogs = [];
			let modifiedSizeList = [];
			const reversedList = grade1 === 1 ? existingSizeList1.reverse() : existingSizeList1;
			let count = 1;

			for (let item of reversedList) {
				let input = event?.target?.value ? Number(event?.target?.value) : 0;
				let newValue =
					grade1 === 1
						? Number(input || 0) - obj.grading1 * count
						: Number(input || 0) + obj.grading2 * count;
				let changeLogInfo = {
					changeLogType: 'MEASUREMENT_VALUE_UPDATE',
					id: obj.fitTypeId,
					dto: {
						pointOfMeasurementId: obj.pomResponseId,
						value: newValue || null,
						sizeId: item.id
					}
				};
				bulkChangeLogs.push(changeLogInfo);
				modifiedSizeList.push({ ...item, value: newValue });
				count++;
			}
			await saveBulkSizeChanges(obj, bulkChangeLogs, modifiedSizeList);
		}
		if (item && obj && item.id === obj.baseSize && obj.grading2) {
			if (grade2 === 2) {
				let bulkChangeLogs = [];
				let modifiedSizeList = [];

				// const reversedList = grade === 1 ? existingSizeList.reverse() : existingSizeList;
				const reversedList = existingSizeList2;
				let count = 1;
				for (let i = 0; i < reversedList.length; i++) {
					let newValue = Number(input2 || 0) + Number(obj.grading2) * count;
					let changeLogInfo = {
						changeLogType: 'MEASUREMENT_VALUE_UPDATE',
						id: obj.fitTypeId,
						dto: {
							pointOfMeasurementId: obj.pomResponseId,
							value: newValue || null,
							sizeId: reversedList[i].id
						}
					};
					bulkChangeLogs.push(changeLogInfo);
					modifiedSizeList.push({ ...reversedList[i], value: newValue });
					count++;
				}
				let count3 = 1;
				if (grate3 === 3 && existingSizeList3.length > 0 && obj.grading2 && obj.grading3) {
					//let kkk = Number(input2 || 0) + Number(obj.grading2 * (count - 1));
					for (let i = 0; i < existingSizeList3.length; i++) {
						let newValue =
							Number(input2 || 0) +
							Number(obj.grading2) * count +
							+Number(obj.grading3) * count3 -
							Number(obj.grading2) * count3;

						let changeLogInfo = {
							changeLogType: 'MEASUREMENT_VALUE_UPDATE',
							id: obj.fitTypeId,
							dto: {
								pointOfMeasurementId: obj.pomResponseId,
								value: newValue || null,
								sizeId: existingSizeList3[i].id
							}
						};
						bulkChangeLogs.push(changeLogInfo);
						modifiedSizeList.push({ ...existingSizeList3[i], value: newValue });
						count++;
						count3++;
					}
				}
				await saveBulkSizeChanges(obj, bulkChangeLogs, modifiedSizeList);
			}
		}
		if (item && obj && obj.grading3 && baseSize3.id === item.id) {
			if (grate3 === 3) {
				let bulkChangeLogs = [];
				let modifiedSizeList = [];
				let count5 = 1;
				for (let i = 0; i < existingSizeList3.length; i++) {
					let newValue = Number(input2 || 0) + obj.grading3 * count5;
					let changeLogInfo = {
						changeLogType: 'MEASUREMENT_VALUE_UPDATE',
						id: obj.fitTypeId,
						dto: {
							pointOfMeasurementId: obj.pomResponseId,
							value: newValue || null,
							sizeId: existingSizeList3[i].id
						}
					};
					bulkChangeLogs.push(changeLogInfo);
					modifiedSizeList.push({ ...existingSizeList3[i], value: newValue });
					count5++;
				}
				await saveBulkSizeChanges(obj, bulkChangeLogs, modifiedSizeList);
			}
		}
	};

	const changeMeasurementGradingType = async (value, obj, fitType) => {
		if (isEditable()) {
			let changeLogInfo = {
				changeLogType: 'GRADING_ACTION_CHANGE',
				id: obj?.fitTypeId,
				dto: {
					pointOfMeasurementId: obj.pomResponseId,
					gradingType: value.constant
				}
			};
			await dispatch(saveProductMeasurementChangeLog(changeLogInfo));
			await dispatch(
				modifyMeasurementTableCellValue({
					fieldName: 'gradingType',
					fitType,
					pointId: obj.pomResponseId,
					value: value
				})
			);

			const gradingFunctions = {
				PARALLEL_PROPORTIONAL: onChangeParallelAndProportionalGrading,
				PEER: onChangePeerGrading,
				GROUP: onChangeGroupGrading
			};
			const gradingKeys = ['FIRST', 'SECOND', 'THIRD'];
			const constant = value.constant;
			if (gradingFunctions[constant]) {
				for (const key of gradingKeys) {
					let gradingNumber = key === 'FIRST' ? 1 : key === 'SECOND' ? 2 : 3;
					if (obj?.[`grading${gradingNumber}`]) {
						await gradingFunctions[constant](obj?.[`grading${gradingNumber}`], obj, key);
					}
				}
			}
		}
	};
	const getGradingInfos = (gradeNumber, obj) => {
		let grade = gradeNumber === 'FIRST' ? 1 : gradeNumber === 'SECOND' ? 2 : 3;
		let sizeList = sortBySerial(obj?.sizeValueList);
		let baseSizeIndex = sizeList?.findIndex((item) => item.id === obj?.baseSize);
		let groupBaseSizeIndex = null;
		if (fitWiseSizeList?.FIRST_FIT?.groupGradingBaseSize) {
			groupBaseSizeIndex = sizeList?.findIndex(
				(item) => item.id === fitWiseSizeList?.FIRST_FIT?.groupGradingBaseSize
			);
		}
		let baseSize = sizeList[baseSizeIndex];
		let existingSizeList = [];

		if (groupBaseSizeIndex !== null && grade === 3) {
			baseSize = sizeList[groupBaseSizeIndex];
		}

		if (grade === 1) {
			existingSizeList = sizeList.slice(0, baseSizeIndex);
		}
		if (grade === 2) {
			if (groupBaseSizeIndex !== null) {
				existingSizeList = sizeList.slice(baseSizeIndex + 1, groupBaseSizeIndex + 1);
			} else {
				existingSizeList = sizeList.slice(baseSizeIndex + 1);
			}
		}
		if (grade === 3 && groupBaseSizeIndex !== null) {
			existingSizeList = sizeList.slice(groupBaseSizeIndex + 1);
		}
		return {
			grade,
			baseSize,
			baseSizeIndex,
			existingSizeList
		};
	};

	const saveBulkSizeChanges = async (obj, bulkChangeLogs, modifiedSizeList) => {
		await dispatch(saveProductMeasurementBulkChangeLog(bulkChangeLogs));
		await dispatch(
			saveProductMeasurementBulkModifiedSizes({
				pointId: obj.pomResponseId,
				fitType: 'FIRST_FIT',
				modifiedSizeList: modifiedSizeList
			})
		);
	};

	const onChangeGroupGrading = async (inputValue, obj, gradeNumber) => {
		const { grade, baseSize, existingSizeList } = getGradingInfos(gradeNumber, obj);
		let bulkChangeLogs = [];
		let modifiedSizeList = [];
		const reversedList = grade === 1 ? existingSizeList.reverse() : existingSizeList;
		for (let item of reversedList) {
			let input = inputValue ? Number(inputValue) : 0;
			let newValue =
				grade === 1 ? Number(baseSize?.value || 0) - input : Number(baseSize?.value || 0) + input;
			let changeLogInfo = {
				changeLogType: 'MEASUREMENT_VALUE_UPDATE',
				id: obj.fitTypeId,
				dto: {
					pointOfMeasurementId: obj.pomResponseId,
					value: newValue || null,
					sizeId: item.id
				}
			};
			bulkChangeLogs.push(changeLogInfo);
			modifiedSizeList.push({ ...item, value: newValue });
		}
		await saveBulkSizeChanges(obj, bulkChangeLogs, modifiedSizeList);
	};

	const onChangeParallelAndProportionalGrading = async (inputValue, obj, gradeNumber) => {
		const { grade, baseSize, existingSizeList } = getGradingInfos(gradeNumber, obj);

		let bulkChangeLogs = [];
		let modifiedSizeList = [];
		const reversedList = grade === 1 ? existingSizeList.reverse() : existingSizeList;
		let count = 1;
		for (let item of reversedList) {
			let input = inputValue ? Number(inputValue) : 0;
			let newValue =
				grade === 1
					? Number(baseSize?.value || 0) - input * count
					: Number(baseSize?.value || 0) + input * count;
			let changeLogInfo = {
				changeLogType: 'MEASUREMENT_VALUE_UPDATE',
				id: obj.fitTypeId,
				dto: {
					pointOfMeasurementId: obj.pomResponseId,
					value: newValue || null,
					sizeId: item.id
				}
			};
			bulkChangeLogs.push(changeLogInfo);
			modifiedSizeList.push({ ...item, value: newValue });
			count++;
		}
		await saveBulkSizeChanges(obj, bulkChangeLogs, modifiedSizeList);
	};

	const onChangePeerGrading = async (inputValue, obj, gradeNumber) => {
		const { grade, baseSize, existingSizeList } = getGradingInfos(gradeNumber, obj);

		let bulkChangeLogs = [];
		let modifiedSizeList = [];
		const reversedList = grade === 1 ? existingSizeList.reverse() : existingSizeList;
		let count = 1;

		const addChangeLog = (newValue, sizeId) => {
			return {
				changeLogType: 'MEASUREMENT_VALUE_UPDATE',
				id: obj.fitTypeId,
				dto: {
					pointOfMeasurementId: obj.pomResponseId,
					value: newValue ? newValue : null,
					sizeId: sizeId
				}
			};
		};

		for (let i = 0; i < reversedList.length; i++) {
			let firstPeer = reversedList[i];
			let secondPeer = (grade === 2 || grade === 3) && i === 0 ? {} : reversedList[i + 1];
			let input = inputValue ? Number(inputValue) : 0;
			let newValue =
				grade === 1
					? Number(baseSize?.value || 0) - input * count
					: Number(baseSize?.value || 0) + input * count;

			if (firstPeer?.id) {
				let modifiedValue = (grade === 2 || grade === 3) && i === 0 ? baseSize?.value || 0 : newValue;

				bulkChangeLogs.push(addChangeLog(modifiedValue, firstPeer.id));
				modifiedSizeList.push({
					...firstPeer,
					value: modifiedValue
				});
			}

			if (secondPeer?.id) {
				bulkChangeLogs.push(addChangeLog(newValue, secondPeer.id));
				modifiedSizeList.push({ ...secondPeer, value: newValue });
			}
			if (!((grade === 2 || grade === 3) && i === 0)) {
				i++;
				count++;
			}
		}
		await saveBulkSizeChanges(obj, bulkChangeLogs, modifiedSizeList);
	};

	const saveGradingLog = async (obj, gradingNumber) => {
		let changeLogInfo = {
			changeLogType: 'GRADING_ADD',
			id: obj?.fitTypeId,
			dto: {
				pointOfMeasurementId: obj.pomResponseId,
				value: null,
				gradingType: gradingNumber === 'THIRD' ? 'GROUP' : obj.gradingType?.constant,
				gradingNumber
			}
		};
		await dispatch(saveProductMeasurementChangeLog(changeLogInfo));
	};

	const onChangeGrading = async (e, obj, gradingNumber) => {
		e.preventDefault();
		if (isEditable() && obj?.gradingType?.constant) {
			let value = e.target.value;
			let changeLogInfo = {
				changeLogType: 'GRADING_ADD',
				id: obj?.fitTypeId,
				dto: {
					pointOfMeasurementId: obj.pomResponseId,
					value: value ? value : null,
					gradingType: gradingNumber === 'THIRD' ? 'GROUP' : obj.gradingType?.constant,
					gradingNumber
				}
			};
			await dispatch(saveProductMeasurementChangeLog(changeLogInfo));

			if (obj.gradingType.constant === 'PARALLEL_PROPORTIONAL') {
				await onChangeParallelAndProportionalGrading(value, obj, gradingNumber);
			}
			if (obj.gradingType.constant === 'PEER') {
				await onChangePeerGrading(value, obj, gradingNumber);
			}
			if (obj.gradingType.constant === 'GROUP') {
				await onChangeGroupGrading(value, obj, gradingNumber);
			}
		}
	};

	const isModified = (fieldName = false) => {
		return fieldName ? 'highlight-cell' : '';
	};

	const formatNumber = (num) => {
		if (num !== undefined && num !== '') {
			let result = Number(num).toFixed(2);
			result = parseFloat(result);
			return result;
		} else {
			return '';
		}
	};

	const renderTableCell = (fitInfo) => {
		//console.log('getTargetValue:',getTargetValue())
		if (fitInfo === undefined) return <></>;

		// Render Actual value & Difference
		const renderActualAndDifferColumns = (obj, fitType) => {
			let isSizeExist = fitWiseSizeList['FIRST_FIT']?.sizeList.length > 0;
			if (
				isSizeExist &&
				selectedMeasurementType.constant !== 'COSTING_GRADING' &&
				hasShowActualAndDiffColumn
			) {
				const getDifference = () => {
					let total = parseFloat((obj?.actualValue || 0) - getTotalTarget(obj).toFixed(2));
					return Math.sign(total) === -1 ? total : total > 0 ? `+${total}` : '';
				};
				let difference = obj?.actualValue ? getDifference() : '';
				return (
					<>
						{/* Render Actual Value */}
						<td
							onClick={() =>
								handleSelectedRow(
									obj.pomResponseId,
									'actualValue',
									fitType,
									formatNumber(obj?.actualValue)
								)
							}
							className={isModified(obj?.actualValueModified)}
						>
							{isShowInput(obj.pomResponseId, fitType, 'actualValue') ? (
								<input
									type="number"
									min={1}
									name="actualValue"
									autoFocus={true}
									disabled={
										isInputDisabled(obj) ||
										!getTargetValue(obj?.sizeValueList, obj?.baseSize)
									}
									value={formatNumber(obj?.actualValue)}
									style={{ display: 'block' }}
									onChange={(e) =>
										handleChange(e, obj.pomResponseId, 'actualValue', fitType)
									}
									onBlur={(e) =>
										updateToleranceOrActualValue(
											e,
											obj.pomResponseId,
											'ACTUAL_VALUE_UPDATE',
											obj.fitTypeId
										)
									}
									onKeyPress={validateFloatNumber}
									onKeyDown={(e) => onPressKey(e, obj, fitType)}
								/>
							) : (
								<p>{formatNumber(obj?.actualValue)}</p>
							)}
						</td>
						{/* **************** */}
						{/* Render Difference */}
						<td
							className={`difference-td ${
								Math.abs(difference) > Math.abs(formatNumber(obj?.tolerance))
									? 'wrong-input'
									: ''
							}`}
						>
							<p className="red">
								{difference === 0
									? ''
									: difference > 0
										? `+${formatNumber(difference)}`
										: formatNumber(difference)}
							</p>
						</td>
						{/* **************** */}
					</>
				);
			}
		};
		const renderSizeValueList = (obj, fitType) => {
			let sizeValueList = sortBySerial(obj?.sizeValueList);
			// let sizeValueList = obj?.sizeValueList;
			if (sizeValueList === undefined) return <></>;
			return sizeValueList?.map((item, index) => {
				return (
					<React.Fragment key={`size_cell_${index}`}>
						{!hiddenColumnIds.includes(item.id) && (
							<td
								className={`${
									obj?.baseSize === item.id ||
									fitWiseSizeList?.FIRST_FIT?.groupGradingBaseSize === item.id
										? 'target'
										: ''
								} ${isModified(item?.modified || item.sizeModified)}`}
								onClick={() =>
									handleSelectedRow(
										obj.pomResponseId,
										`sizeValue_${index}`,
										fitType,
										formatNumber(item?.value)
									)
								}
							>
								{isShowInput(obj.pomResponseId, fitType, `sizeValue_${index}`) ? (
									<input
										type="number"
										min={1}
										autoFocus={true}
										name={`sizeValue_${index}`}
										disabled={isInputDisabled(obj)}
										value={formatNumber(item?.value)}
										style={{ display: 'block' }}
										onChange={(e) =>
											handleSizeChange(
												e,
												obj.pomResponseId,
												'sizeValueList',
												fitType,
												obj?.sizeValueList,
												item.id
											)
										}
										onBlur={(e) => {
											onBlurKey(e, obj, 'MEASUREMENT_VALUE_UPDATE', item);
										}}
										onKeyPress={validateFloatNumber}
										onKeyDown={(e) =>
											onPressKey(e, obj, fitType, 'MEASUREMENT_VALUE_UPDATE', item)
										}
									/>
								) : (
									<p>{formatNumber(item?.value)}</p>
								)}
							</td>
						)}
						{item.id === obj.baseSize && renderActualAndDifferColumns(obj, fitType)}
					</React.Fragment>
				);
			});
		};

		const getTotalTarget = (obj) => {
			let sizeValueList = obj?.sizeValueList;
			const baseSizeObj = sizeValueList.find((item) => item.id === obj.baseSize);
			return baseSizeObj?.value ? Number(baseSizeObj.value) : 0;
		};

		const isShowInput = (pomResponseId, fitType, fieldType) => {
			return (
				selectedRow.pomResponseId === pomResponseId &&
				selectedRow.fitType === fitType &&
				selectedRow.fieldType === fieldType
			);
		};

		const renderGradingCell = (obj, fitType) => {
			return (
				<>
					{Object.keys(gradings).length > 0 && (
						<td
							onClick={() =>
								handleSelectedRow(
									obj.pomResponseId,
									'grading1',
									fitType,
									formatNumber(obj?.grading1)
								)
							}
							className={`grading ${isModified(obj?.grading1Modified)}`}
						>
							{isShowInput(obj.pomResponseId, fitType, 'grading1') ? (
								<input
									type="number"
									value={formatNumber(obj?.grading1)}
									style={{ display: 'block' }}
									name="grading1"
									disabled={
										!isBaseSizeExist() ||
										!isBaseSizeValueExist(obj) ||
										isInputDisabled(obj) ||
										(Object.keys(selectedGroupGrade).length === 0 &&
											!obj.gradingType?.constant)
									}
									autoFocus={true}
									// Grading 1 change
									onChange={(e) => handleChange(e, obj.pomResponseId, 'grading1', fitType)}
									onBlur={async (e) => {
										if (e.target?.value) {
											await onChangeGrading(e, obj, 'FIRST');
										} else {
											await saveGradingLog(obj, 'FIRST');
										}
									}}
									onKeyPress={validateFloatNumber}
									onKeyDown={(e) => onPressKey(e, obj, fitType)}
								/>
							) : (
								<p>{formatNumber(obj?.grading1)}</p>
							)}
						</td>
					)}
					{Object.keys(gradings).length > 0 && (
						<td
							className={`grading ${isModified(obj?.grading2Modified)}`}
							onClick={() =>
								handleSelectedRow(
									obj.pomResponseId,
									'grading2',
									fitType,
									formatNumber(obj?.grading2)
								)
							}
						>
							{isShowInput(obj.pomResponseId, fitType, 'grading2') ? (
								<input
									type="number"
									value={formatNumber(obj?.grading2)}
									style={{ display: 'block' }}
									name="grading2"
									disabled={
										!isBaseSizeExist() ||
										!isBaseSizeValueExist(obj) ||
										isInputDisabled(obj) ||
										(Object.keys(selectedGroupGrade).length === 0 &&
											!obj.gradingType?.constant)
									}
									autoFocus={true}
									// Grading 2 change
									onChange={(e) => handleChange(e, obj.pomResponseId, 'grading2', fitType)}
									onBlur={async (e) => {
										if (e.target?.value) {
											await onChangeGrading(e, obj, 'SECOND');
										} else {
											await saveGradingLog(obj, 'SECOND');
										}
									}}
									onKeyPress={validateFloatNumber}
									onKeyDown={(e) => onPressKey(e, obj, fitType)}
								/>
							) : (
								<p>{formatNumber(obj?.grading2)}</p>
							)}
						</td>
					)}
					<td
						className={`grading ${isModified(obj?.grading3Modified)}`}
						onClick={() =>
							handleSelectedRow(
								obj.pomResponseId,
								'grading3',
								fitType,
								formatNumber(obj?.grading3)
							)
						}
					>
						{isShowInput(obj.pomResponseId, fitType, 'grading3') ? (
							<input
								type="number"
								value={formatNumber(obj?.grading3)}
								style={{ display: 'block' }}
								name="grading3"
								disabled={
									Object.keys(selectedGroupGrade).length === 0 ||
									!isBaseSizeExist() ||
									!isGroupGradeBaseSizeValueExist(obj) ||
									isInputDisabled(obj)
								}
								autoFocus={true}
								// Grading 3 change
								onChange={(e) => handleChange(e, obj.pomResponseId, 'grading3', fitType)}
								onBlur={async (e) => {
									if (e.target?.value) {
										await onChangeGrading(e, obj, 'THIRD');
									} else {
										await saveGradingLog(obj, 'THIRD');
									}
								}}
								onKeyPress={validateFloatNumber}
								onKeyDown={(e) => onPressKey(e, obj, fitType)}
							/>
						) : (
							<p>{formatNumber(obj?.grading3)}</p>
						)}
					</td>
					<td className={isModified(obj?.gradingTypeModified)}>
						<div className="download-dropdown dropdown mr-12">
							<button
								disabled={
									!isBaseSizeExist() ||
									!isEditable() ||
									!isBaseSizeValueExist(obj) ||
									isInputDisabled(obj)
								}
								className="button text size32"
								type="button"
								id="downloadType"
								data-toggle="dropdown"
								aria-expanded="false"
							>
								<span className="gray_dark_02">
									{obj?.gradingType?.name ? obj?.gradingType?.name : 'Select'}
								</span>
								<img
									src={`${process.env.PUBLIC_URL}/images/chevron_down.svg`}
									alt="download"
									className="ml-2"
								/>
							</button>
							<ul
								className="dropdown-menu dropdown-menu-right shadow-lg"
								role="menu"
								aria-labelledby="downloadType"
							>
								{gradingTypes?.map((item, index) => {
									return (
										<li
											key={`grading_type_${index}`}
											onClick={() => changeMeasurementGradingType(item, obj, fitType)}
										>
											<span>{item.name}</span>
										</li>
									);
								})}
							</ul>
						</div>
					</td>
				</>
			);
		};

		const getTargetValue = (existingSizeList, sizeId) => {
			const targetItem = existingSizeList?.find((item) => item?.id === sizeId);

			// If targetItem exists, return its value, otherwise return undefined or any fallback value.
			return targetItem ? targetItem.value : undefined;
		};

		return Object?.keys(fitInfo)?.map((item, index) => {
			let fitType = item;
			let obj = fitInfo[item];
			return (
				<React.Fragment key={`table_cel_${index}`}>
					{index === 0 && (
						<td
							onClick={() =>
								handleSelectedRow(
									obj.pomResponseId,
									'tolerance',
									fitType,
									formatNumber(obj?.tolerance)
								)
							}
							className={isModified(obj?.toleranceModified)}
						>
							{isShowInput(obj.pomResponseId, fitType, 'tolerance') ? (
								<input
									type="number"
									min={1}
									value={formatNumber(obj?.tolerance)}
									style={{ display: 'block' }}
									name="tolerance"
									autoFocus={true}
									disabled={
										isInputDisabled(obj) ||
										!getTargetValue(obj?.sizeValueList, obj?.baseSize)
									}
									onChange={(e) => handleChange(e, obj.pomResponseId, 'tolerance', fitType)}
									onBlur={(e) =>
										updateToleranceOrActualValue(
											e,
											obj.pomResponseId,
											'TOLERANCE_UPDATE',
											obj.fitTypeId
										)
									}
									onKeyPress={validateFloatNumber}
									onKeyDown={(e) => onPressKey(e, obj, fitType)}
								/>
							) : (
								<p>{formatNumber(obj?.tolerance)}</p>
							)}
						</td>
					)}
					{renderSizeValueList(obj, fitType)}
					<td
						onClick={() =>
							handleSelectedRow(obj.pomResponseId, 'remarks', fitType, obj?.remarks || '')
						}
						className={isModified(obj?.remarksModified)}
					>
						{isShowInput(obj.pomResponseId, fitType, 'remarks') ? (
							<textarea
								className="table-column-textarea"
								autoFocus={true}
								name="remarks"
								disabled={isInputDisabled(obj)}
								value={obj?.remarks || ''}
								onChange={(e) => handleChange(e, obj.pomResponseId, 'remarks', fitType)}
								onBlur={(e) =>
									handleOnBlurChangeLog(
										e,
										'POINT_REMARKS_UPDATE',
										'remarks',
										obj.pomResponseId,
										obj.fitTypeId
									)
								}
								onKeyDown={(e) => onPressKey(e, obj, fitType)}
							/>
						) : (
							obj?.remarks
						)}
					</td>
					{renderGradingCell(obj, fitType)}
				</React.Fragment>
			);
		});
	};

	const handleChangePomResponse = (e, pointId) => {
		if (isEditable()) {
			dispatch(
				modifyMeasurementTablePointName({
					pointId,
					value: e.target.value
				})
			);
		}
	};

	const handleOnBlurChangeLog = (e, logType, fieldName, pointId, fitTypeId) => {
		if (isEditable() && selectedRow?.defaultValue !== e.target.value) {
			let changeLogInfo = {
				changeLogType: logType,
				id: fitTypeId,
				dto: {
					pointOfMeasurementId: pointId,
					[fieldName]: e.target.value
				}
			};
			dispatch(saveProductMeasurementChangeLog(changeLogInfo));
		}
	};

	const isInputDisabled = (obj) => {
		return ['APPROVED', 'REJECTED'].includes(obj?.status) && !isAdmin();
	};

	const sortBySerial = (arrayList) => {
		if (arrayList) {
			let modifiedArrayList = arrayList.map((item) => {
				if ([item.sizeCategoryId] in sizeCategorySequence) {
					item.sizeCategorySerial = sizeCategorySequence[item.sizeCategoryId];
				} else {
					item.sizeCategorySerial = 1;
				}
				return item;
			});
			return modifiedArrayList.slice().sort((a, b) => {
				if (a.sizeCategorySerial !== b.sizeCategorySerial) {
					return a.sizeCategorySerial - b.sizeCategorySerial;
				}
				if (a.sizeCategoryId !== b.sizeCategoryId) {
					return a.sizeCategoryId - b.sizeCategoryId;
				}
				return a.serial - b.serial;
			});
		}
	};

	const onPOMSelect = (obj) => {
		const [pomCode, name] = obj.value.split(': ');
		onReplacePOM(obj.id, showReplaceDropdownId);
		const updatedItem = tableData.find((item) => item.pomResponse.id === showReplaceDropdownId);
		if (updatedItem) {
			dispatch(
				modifyMeasurementTablePointCode({
					pointId: showReplaceDropdownId,
					value: pomCode
				})
			);
			dispatch(
				modifyMeasurementTablePointName({
					pointId: showReplaceDropdownId,
					value: name
				})
			);
			dispatch(
				modifyMeasurementTablePointIds({
					pointId: showReplaceDropdownId,
					value: obj.id,
					referenceId: obj.referenceId
				})
			);
		}

		setShowReplaceDropdownId(null);
	};

	const renderTableBody = () => {
		return tableData
			?.filter((item) => item?.pomResponse?.id)
			?.map((item, index) => {
				if (item?.pomResponse?.id) {
					return (
						<tr
							key={`measurement_table_row_${index}`}
							onDragStart={(e) => handleDragStart(e, index)}
							onDragOver={handleDragOver}
							onDrop={(e) => handleDrop(e, index)}
							draggable
							className={
								item?.pomResponseModified || item?.pomResponseOrderingModified
									? 'highlight-row'
									: ''
							}
						>
							{/* First column named CODE */}
							<td>
								<div className="d-flex justify-content-between align-items-center">
									<span>
										{isEditable() && showReplaceDropdownId === item?.pomResponse?.id ? (
											<div className="transparent-select">
												<CustomDropdown
													isShowLabel={false}
													searchPlaceholder={'POM code or name'}
													toolTipLength={18}
													items={getAllPomOptions()}
													onItemClick={onPOMSelect}
												/>
											</div>
										) : (
											<span
												onClick={() =>
													setShowReplaceDropdownId(item?.pomResponse?.id)
												}
											>
												{item?.pomResponse?.pomCode || item?.pomResponse?.id}
											</span>
										)}
									</span>

									{isEditable() && (
										<div className="option">
											<div className="dropdown">
												<button
													className="btn btn-default dropdown-toggle p-0"
													type="button"
													id="menu1"
													data-toggle="dropdown"
													aria-expanded="false"
												>
													<img
														src={`${process.env.PUBLIC_URL}/icons/more_vertical.svg`}
														alt="tee"
													/>
												</button>

												<ul
													className="dropdown-menu dropdown-menu-right shadow-lg"
													role="menu"
													aria-labelledby="menu1"
													x-placement="bottom-end"
												>
													{item?.pomResponse?.referenceId && (
														<li
															onClick={() =>
																setSelectedPom(item?.pomResponse?.referenceId)
															}
														>
															<span>View POM</span>
														</li>
													)}

													{!item?.pomResponse?.isDuplicated &&
														item?.pomResponse?.referenceId && (
															<li
																onClick={() =>
																	onDuplicateMeasurement(item, index)
																}
															>
																<span>Duplicate</span>
															</li>
														)}
													<li
														onClick={() =>
															setShowReplaceDropdownId(item?.pomResponse?.id)
														}
													>
														<span>Replace</span>
													</li>
													<li onClick={() => onPomRemove(item.pomResponse)}>
														<span role="menuitem" className=" red">
															Remove
														</span>
													</li>
												</ul>
											</div>
										</div>
									)}
								</div>
							</td>
							{/* ************************* */}
							{/* Second column named Points */}
							<td className={isModified(item?.pomResponseNameModified)}>
								<div className="measurement-row-delete d-flex justify-content-between">
									<div>
										{selectedRow.pomResponseId === item?.pomResponse?.id &&
										selectedRow.fieldType === 'pomName' ? (
											<textarea
												className="table-column-textarea"
												autoFocus={true}
												name="pomName"
												value={item?.pomResponse?.name || ''}
												onChange={(e) =>
													handleChangePomResponse(e, item.pomResponse.id)
												}
												onBlur={(e) =>
													handleOnBlurChangeLog(
														e,
														'POINT_NAME_UPDATE',
														'name',
														item.pomResponse.id,
														item.fitInfo['FIRST_FIT'].fitTypeId
													)
												}
												onKeyDown={(e) =>
													onPressKey(e, {
														pomResponseId: item.pomResponse.id,
														fitTypeId: item.fitInfo['FIRST_FIT'].fitTypeId
													})
												}
											/>
										) : (
											<span
												className="d-inline-block align-middle"
												onClick={() =>
													handleSelectedRow(
														item?.pomResponse?.id,
														'pomName',
														'',
														item?.pomResponse?.name || ''
													)
												}
											>
												{item?.pomResponse?.name}
											</span>
										)}
									</div>
								</div>
							</td>
							{/* ************************* */}
							{renderTableCell(item.fitInfo)}
						</tr>
					);
				}
			});
	};

	const renderGroupGradingDropDown = (gradingId) => {
		if (selectedGradingId === gradingId) {
			return (
				<div className="dropdown-menu show action-heading-dropdown" ref={gradingDropDownOptionRef}>
					<ul>
						<li onClick={() => copyGradings(gradingId)}>
							<span>Copy column</span>
						</li>
						{isEditable() && (
							<>
								<li onClick={() => pasteGradings(gradingId)}>
									<span>Paste column</span>
								</li>
							</>
						)}
					</ul>
				</div>
			);
		}
	};

	const renderGradingTableHeading = () => {
		if (Object.keys(gradings).length > 0) {
			return Object.keys(gradings)?.map((item, index) => {
				let currentGrading = gradings[item];
				if (item !== 'grading3') {
					return (
						<React.Fragment key={`grading_${index}`}>
							<th className="cursor-pointer">
								<div
									onClick={() => {
										setSelectedGradingId(item);
									}}
								>
									<p className="white-space-nowrap">Grading {index + 1}</p>
									{currentGrading.length > 1 && (
										<p>
											{currentGrading[0].label}-
											{currentGrading[currentGrading.length - 1].label}
										</p>
									)}
									{currentGrading.length === 1 && <p>{currentGrading[0].label}</p>}
								</div>
								{renderGroupGradingDropDown(item)}
							</th>
						</React.Fragment>
					);
				}
			});
		}
	};

	const renderGrading3 = () => {
		let gradeArr = gradings['grading3'];
		if (gradeArr?.length > 1) {
			return (
				<p>
					{gradeArr[0].label}-{gradeArr[gradeArr.length - 1].label}
				</p>
			);
		} else if (gradeArr?.length === 1) {
			return <p>{gradeArr[0]?.label}</p>;
		}
	};

	const handleHorizontalScroll = (element, speed, distance, step) => {
		let scrollAmount = 0;
		const slideTimer = setInterval(() => {
			element.scrollLeft += step;
			scrollAmount += Math.abs(step);
			if (scrollAmount >= distance) {
				clearInterval(slideTimer);
			}
			if (element.scrollLeft === 0) {
				setArrowDisable(true);
			} else {
				setArrowDisable(false);
			}
		}, speed);
	};

	return (
		<>
			{isShowSlideIcon && (
				<div className="scroll-table-buttons on-measurement">
					<button
						className="left"
						onClick={() => {
							handleHorizontalScroll(elementRef.current, 25, 100, -400);
						}}
						disabled={arrowDisable}
					>
						<SlideIcon />
					</button>
					&nbsp;&nbsp;
					<button
						className="right"
						onClick={() => {
							handleHorizontalScroll(elementRef.current, 25, 100, 400);
						}}
					>
						<SlideIcon />
					</button>
				</div>
			)}
			<div className="measurement-info-table scroll-x-label" ref={elementRef}>
				<table className={`${!isEditable() ? 'edit-table-hover' : ''} table auto`}>
					<thead>
						<tr>
							<th colSpan={generateHeadingColspan()}>
								<div className="d-flex gap-2">
									<p className="regular-14 weight-500 gray_dark_01">
										{selectedMeasurementType?.name}
									</p>
									{pomAndSizeList?.showBlockUsedFromInfo ? (
										<BlockUsedFrom pomAndSizeList={pomAndSizeList} />
									) : null}
									{hiddenColumnIds.length > 0 ? (
										<p
											className="regular-14 weight-500 gray_dark_01 text-underline cursor-pointer"
											onClick={() => onShowAllHiddenSizeColumn()}
										>
											Show hidden column: {hiddenColumnIds.length}
										</p>
									) : null}
								</div>
							</th>
						</tr>
						<tr className="heading-title">
							<th>CODE</th>
							<th>Points</th>
							<th>TOL(+/-)</th>
							{renderSizeHeading()}
							<th>Review Comment</th>
							{renderGradingTableHeading()}
							<th
								className="cursor-pointer"
								onClick={() => {
									if (isEditable()) {
										setShowGradingDropDown(true);
									}
								}}
							>
								{selectedGroupGrade?.id && (
									<>
										<p className="white-space-nowrap">Grading 3</p>
										{renderGrading3()}
									</>
								)}
								{!selectedGroupGrade?.id && (
									<>
										<p className="white-space-nowrap">+ Grading</p>
									</>
								)}
								{renderGradingDropDown()}
							</th>
							<th>Action</th>
						</tr>
					</thead>
					<tbody onBlur={() => handleSelectedRow()}>{renderTableBody()}</tbody>
				</table>
			</div>
			{selectedPom && <PomViewModal pomId={selectedPom} onClose={() => setSelectedPom(null)} />}
		</>
	);
};

export default CostingAndPPSampleTable;
